import { MapPin } from 'lucide-react';
import type { UpcomingLaunch } from '@vtak/mod-space/client';

/** A titled, scrollable list of launches; rows with a pad fly the map on click. */
export function MissionList({
  title,
  launches,
  onSelect,
  emptyText = 'No launches',
}: {
  title: string;
  launches: UpcomingLaunch[];
  onSelect: (launch: UpcomingLaunch) => void;
  /** Message when the list is empty (e.g. "the only launch is featured above"). */
  emptyText?: string;
}): React.JSX.Element {
  return (
    <div className="flex min-h-0 flex-col rounded-lg border border-echo-border bg-echo-grey/80 p-4">
      <div className="mb-2 font-chakra text-xs font-bold uppercase tracking-[0.2em] text-echo-yellow">{title}</div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {launches.length === 0 ? (
          <p className="p-3 text-center text-xs text-muted-foreground">{emptyText}</p>
        ) : (
          launches.map((launch) => <MissionRow key={launch.id} launch={launch} onSelect={onSelect} />)
        )}
      </div>
    </div>
  );
}

/** One launch row. */
function MissionRow({
  launch,
  onSelect,
}: {
  launch: UpcomingLaunch;
  onSelect: (launch: UpcomingLaunch) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onSelect(launch)}
      className="w-full rounded-md border border-echo-border/60 bg-white/[0.03] p-2 text-left transition-colors hover:bg-white/[0.07]"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {launch.webcastLive && <LiveDot />}
          <span className="truncate text-sm font-medium text-foreground">{launch.mission || launch.name}</span>
        </span>
        <span className="shrink-0 text-[11px] text-muted-foreground">{formatDate(launch.net)}</span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
        {[launch.rocket, launch.provider].filter(Boolean).join('  |  ')}
      </div>
      {launch.padName && (
        <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{launch.padName}</span>
        </div>
      )}
    </button>
  );
}

/** Pulsing red dot marking a launch with a live webcast. */
function LiveDot(): React.JSX.Element {
  return (
    <span className="relative flex h-2 w-2 shrink-0" title="Live now">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
    </span>
  );
}

/** Format a launch time as "Mon DD · HH:MM". */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
